/**
 * Guided tour (doc 147 §9 Phase 6).
 *
 * A short orientation for new joiners: welcome → search → entity detail →
 * constellation → impact → Ask Atlas. The step list + navigation + seen-flag
 * persistence are pure / framework-free; `GuidedTour.tsx` renders them.
 */
import type { Route } from './hashRouter.js';

export interface TourStep {
  id: string;
  title: string;
  body: string;
  /** The route this step points the user at. */
  goTo: Route;
}

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Atlas',
    body: 'Atlas is the Brisarx data model, navigable. {{ENTITY_COUNT}} entities across {{DOMAIN_COUNT}} domains — click anything to see how it connects.',
    goTo: { kind: 'welcome' },
  },
  {
    id: 'search',
    title: 'Find anything fast',
    body: 'Press ⌘K from anywhere to search every entity, field and Firestore path — or browse domains with the search below.',
    goTo: { kind: 'catalog' },
  },
  {
    id: 'entity',
    title: 'Three-pane entity detail',
    body: 'Click any entity to see its schema, its relationships in and out, and where it is used. Pin the ones you touch often.',
    goTo: { kind: 'workspace', mode: { kind: 'idle' } },
  },
  {
    id: 'constellation',
    title: 'The workspace',
    body: 'Click a node to focus its 1-hop neighbourhood; hit Enter to open the detail sheet. Press ? to see every shortcut.',
    goTo: { kind: 'workspace', mode: { kind: 'idle' } },
  },
  {
    id: 'impact',
    title: 'Impact & trace',
    body: 'From any entity, run an Impact analysis to see the blast radius of a change, or Trace the shortest path between two entities.',
    goTo: { kind: 'trace' },
  },
  {
    id: 'ask',
    title: 'Ask Atlas',
    body: 'Ask the model questions in plain English. Answers are grounded — Atlas flags any entity name it cites that is not real.',
    goTo: { kind: 'ask' },
  },
];

export const TOUR_SEEN_KEY = 'atlas.tourSeen.v1';

/** Clamp a step index into the valid range. */
export function clampStep(index: number): number {
  return Math.max(0, Math.min(index, TOUR_STEPS.length - 1));
}

/** True when `index` is the final tour step. */
export function isLastStep(index: number): boolean {
  return index >= TOUR_STEPS.length - 1;
}

/** Whether the user has already completed/skipped the tour. */
export function hasSeenTour(storage: Pick<Storage, 'getItem'> = localStorage): boolean {
  try {
    return storage.getItem(TOUR_SEEN_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persist that the user has finished or skipped the tour. */
export function markTourSeen(storage: Pick<Storage, 'setItem'> = localStorage): void {
  try {
    storage.setItem(TOUR_SEEN_KEY, 'true');
  } catch {
    // A storage failure just means the tour shows again — harmless.
  }
}
